import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { usePrefersReducedMotion } from '@/components/nexa-mark/use-prefers-reduced-motion';
import { vendorLabel } from '@/lib/providers/integration-state';
import {
  DIAGRAM,
  PIPS_PER_LINK,
  buildDiagram,
  fitText,
  type DiagramLink,
  type DiagramNode,
  type DiagramTone,
} from '@/lib/providers/integration-diagram';
import type { ProvidersIntegrations } from '@/lib/providers/types';

/**
 * THE DIAGRAM VIEW — "how does information flow between the providers, and what
 * is actually real here?", answered by geometry instead of by badges.
 *
 * The list beside it is the denser, more actionable surface and stays the
 * default. This one exists because one fact does not survive a list at all: a
 * MOCK and a live vendor occupy the same row, with the same layout, and differ
 * only by a word. Here they cannot: a simulated provider is drawn INSIDE the
 * instance boundary and its arrow never reaches the edge, a real one is drawn
 * outside and its arrow visibly crosses through a gate, and a vendor this build
 * merely supports is a ghost with no arrow at all. Animated pips ride only the
 * arrows that exist, so "what carries traffic here" is legible before a word is
 * read. The rule lives in `lib/providers/integration-diagram.ts`; this file
 * draws what that module computed and adds no arithmetic of its own.
 *
 * ── COLOUR ───────────────────────────────────────────────────────────────────
 * The mode vocabulary is the cards': MOCK violet, SANDBOX amber, PRODUCTION
 * red. Tones are tailwind classes with a `dark:` half applied to a `<g>`, and
 * every stroke/fill inside reads `currentColor`; structure (boundary, bus,
 * rules, type) reads the app's own `--border` / `--muted-foreground` /
 * `--foreground` tokens. Nothing here is a colour literal that only works in
 * one scheme.
 *
 * ── MOTION ───────────────────────────────────────────────────────────────────
 * Pips are `<animateMotion>` fed the SAME `d` string the arrow is drawn from, so
 * they cannot diverge from the line under them. Under
 * `prefers-reduced-motion: reduce` there are no pips at all: the arrow becomes a
 * static dashed stroke, which still says "traffic flows here" without moving.
 *
 * ── NO POLLING ───────────────────────────────────────────────────────────────
 * This renders the inventory the section already read once on mount. A
 * descriptor is deployment configuration; it cannot change without a redeploy,
 * and a redeploy reloads the console. No timer, no refetch, no WS room.
 */

/** Mode vocabulary, verbatim from the cards. Both halves of every scheme. */
const TONE_CLASS: Record<DiagramTone, string> = {
  mock: 'text-violet-600 dark:text-violet-400',
  sandbox: 'text-amber-600 dark:text-amber-400',
  production: 'text-rose-600 dark:text-rose-400',
  ghost: 'text-muted-foreground',
  unknown: 'text-amber-600 dark:text-amber-400',
};

const INK = 'hsl(var(--foreground))';
const MUTED = 'hsl(var(--muted-foreground))';
const LINE = 'hsl(var(--border))';
const SURFACE = 'hsl(var(--card))';
const PAGE = 'hsl(var(--background))';

export function IntegrationsDiagram({ inventory }: { inventory: ProvidersIntegrations }) {
  const { t } = useTranslation('network');
  const reducedMotion = usePrefersReducedMotion();
  const uid = useId().replace(/:/g, '');

  const diagram = useMemo(
    () =>
      buildDiagram(inventory, {
        vendor: (category, vendor) => vendorLabel(t, category, vendor),
        category: (category) =>
          t(`providers.integrations.category.${category}`, { defaultValue: category }),
        unknownLane: t('providers.diagram.unknownLane'),
        nothingWired: t('providers.integrations.nothingWired'),
      }),
    [inventory, t],
  );

  // The shape is not readable by a screen reader, so the summary is stated in
  // words: how many categories, how much is wired, how much of that is simulated.
  const { counts } = diagram;
  const summary =
    t('providers.diagram.summary', {
      categories: counts.categories,
      wired: counts.wired,
      mock: counts.mock,
      ghost: counts.ghost,
    }) +
    (counts.unknown > 0 ? t('providers.diagram.summaryUnknown', { count: counts.unknown }) : '');

  const dots = `dots-${uid}`;
  const clip = `inside-${uid}`;

  return (
    <div data-testid="integrations-diagram">
      {/* Wide content scrolls in ITS OWN container (frontend-conventions §18) —
          the page body never scrolls sideways. */}
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <svg
          role="img"
          aria-label={summary}
          viewBox={`0 0 ${diagram.width} ${diagram.height}`}
          className="block h-auto w-full"
          style={{ minWidth: diagram.width }}
          data-testid="integrations-diagram-svg"
          data-motion={reducedMotion ? 'static' : 'flowing'}
        >
          <defs>
            <pattern id={dots} width="16" height="16" patternUnits="userSpaceOnUse">
              <circle cx="1.5" cy="1.5" r="1" fill={MUTED} opacity={0.22} />
            </pattern>
            <clipPath id={clip}>
              <rect
                x={diagram.boundary.x}
                y={diagram.boundary.y}
                width={diagram.boundary.w}
                height={diagram.boundary.h}
                rx={14}
              />
            </clipPath>
          </defs>

          {/* ── The boundary: the whole argument of this view ─────────────── */}
          <g clipPath={`url(#${clip})`}>
            <rect
              x={diagram.boundary.x}
              y={diagram.boundary.y}
              width={diagram.boundary.w}
              height={diagram.boundary.h}
              fill={`url(#${dots})`}
            />
          </g>
          <rect
            x={diagram.boundary.x}
            y={diagram.boundary.y}
            width={diagram.boundary.w}
            height={diagram.boundary.h}
            rx={14}
            fill="none"
            stroke={INK}
            strokeOpacity={0.45}
            strokeWidth={1.5}
            data-testid="integrations-diagram-boundary"
          />

          <Caption x={diagram.boundary.x + 16} y={26} text={t('providers.diagram.insideCaption')} />
          <Caption x={DIAGRAM.outsideX} y={26} text={t('providers.diagram.outsideCaption')} />

          {/* ── Lanes: one per category, plus the unknown lane on top ─────── */}
          {diagram.lanes.map((lane) => (
            <g key={lane.id} data-testid={`integrations-diagram-lane-${lane.id}`}>
              <line
                x1={diagram.boundary.x + 10}
                x2={diagram.width - 10}
                y1={lane.y}
                y2={lane.y}
                stroke={LINE}
                strokeDasharray="2 4"
              />
              <text
                x={DIAGRAM.laneLabelX}
                y={lane.labelY}
                fontSize={9.5}
                letterSpacing={0.8}
                fontWeight={600}
                fill={MUTED}
              >
                {lane.label.toLocaleUpperCase()}
              </text>
            </g>
          ))}

          {/* ── The instance core and its internal bus ────────────────────── */}
          {diagram.bus.y1 - diagram.bus.y0 > 1 ? (
            <line
              x1={diagram.bus.x}
              x2={diagram.bus.x}
              y1={diagram.bus.y0}
              y2={diagram.bus.y1}
              stroke={INK}
              strokeOpacity={0.35}
              strokeWidth={2}
              strokeLinecap="round"
            />
          ) : null}
          <path
            d={`M${diagram.core.x + diagram.core.w},${diagram.core.y + diagram.core.h / 2} L${diagram.bus.x},${diagram.core.y + diagram.core.h / 2}`}
            stroke={INK}
            strokeOpacity={0.35}
            strokeWidth={2}
            fill="none"
          />
          <g data-testid="integrations-diagram-core">
            <rect
              x={diagram.core.x}
              y={diagram.core.y}
              width={diagram.core.w}
              height={diagram.core.h}
              rx={10}
              fill={SURFACE}
              stroke={INK}
              strokeOpacity={0.55}
              strokeWidth={1.5}
            />
            <text
              x={diagram.core.x + diagram.core.w / 2}
              y={diagram.core.y + diagram.core.h / 2 - 4}
              textAnchor="middle"
              fontSize={12.5}
              fontWeight={600}
              fill={INK}
            >
              {t('providers.diagram.core')}
            </text>
            <text
              x={diagram.core.x + diagram.core.w / 2}
              y={diagram.core.y + diagram.core.h / 2 + 14}
              textAnchor="middle"
              fontSize={10}
              fill={MUTED}
            >
              {t('providers.diagram.coreWired', { count: counts.wired })}
            </text>
          </g>

          {/* ── Flow: only where traffic actually goes ────────────────────── */}
          {diagram.links.map((l) => (
            <Flow key={l.id} link={l} reducedMotion={reducedMotion} />
          ))}

          {/* ── Gates: where a real call leaves the instance ──────────────── */}
          {diagram.links
            .filter((l) => l.crossing)
            .map((l) => (
              <g
                key={`gate-${l.id}`}
                className={TONE_CLASS[l.tone]}
                data-testid={`integrations-diagram-gate-${l.nodeId}`}
              >
                {/* Punch the boundary line out so the crossing reads as a gate. */}
                <rect
                  x={(l.crossing?.x ?? 0) - 5}
                  y={(l.crossing?.y ?? 0) - 8}
                  width={10}
                  height={16}
                  fill={PAGE}
                />
                <path
                  d={`M${l.crossing?.x},${(l.crossing?.y ?? 0) - 6} L${(l.crossing?.x ?? 0) + 4.5},${l.crossing?.y} L${l.crossing?.x},${(l.crossing?.y ?? 0) + 6} L${(l.crossing?.x ?? 0) - 4.5},${l.crossing?.y} Z`}
                  fill={SURFACE}
                  stroke="currentColor"
                  strokeWidth={1.25}
                />
              </g>
            ))}

          {/* ── Nodes ────────────────────────────────────────────────────── */}
          {diagram.nodes.map((node) => (
            <Node key={node.id} node={node} modeLabel={modeLabelOf(node, t)} />
          ))}
        </svg>
      </div>

      <Legend />
    </div>
  );
}

/** The uppercase strap over a column. */
function Caption({ x, y, text }: { x: number; y: number; text: string }) {
  return (
    <text x={x} y={y} fontSize={9.5} letterSpacing={0.9} fontWeight={600} fill={MUTED}>
      {text.toLocaleUpperCase()}
    </text>
  );
}

/**
 * One arrow, and the pips riding it.
 *
 * The pips are fed `link.d` — the very string the `<path>` is drawn from — so
 * the travelling dots and the line under them cannot drift apart. Under reduced
 * motion the pips are simply not rendered and the stroke goes dashed: still
 * legibly "traffic", with nothing moving.
 */
function Flow({ link, reducedMotion }: { link: DiagramLink; reducedMotion: boolean }) {
  // An unreachable declarant is drawn as a BROKEN link — a gap with a warning in
  // it. Nothing rides it, because we do not know that anything does.
  if (link.broken) {
    const { gapX, y } = link.broken;
    return (
      <g
        className={TONE_CLASS[link.tone]}
        data-testid={`integrations-diagram-link-${link.nodeId}`}
        data-crosses="false"
        data-broken="true"
      >
        <path
          d={`M${DIAGRAM.busX},${y} L${gapX - 11},${y}`}
          stroke="currentColor"
          strokeWidth={1.75}
          strokeDasharray="5 4"
          fill="none"
        />
        <path
          d={`M${gapX + 11},${y} L${DIAGRAM.insideX - DIAGRAM.headLength},${y}`}
          stroke="currentColor"
          strokeWidth={1.75}
          strokeDasharray="5 4"
          fill="none"
        />
        <path d={link.head} fill="currentColor" opacity={0.6} />
        <path
          d={`M${gapX},${y - 7} L${gapX + 7.5},${y + 6} L${gapX - 7.5},${y + 6} Z`}
          fill={PAGE}
          stroke="currentColor"
          strokeWidth={1.25}
          strokeLinejoin="round"
        />
        <path d={`M${gapX},${y - 2} L${gapX},${y + 1.5}`} stroke="currentColor" strokeWidth={1.4} />
        <circle cx={gapX} cy={y + 3.8} r={0.8} fill="currentColor" />
      </g>
    );
  }

  return (
    <g
      className={TONE_CLASS[link.tone]}
      data-testid={`integrations-diagram-link-${link.nodeId}`}
      data-crosses={link.crosses ? 'true' : 'false'}
      data-tone={link.tone}
    >
      <path
        d={link.d}
        stroke="currentColor"
        strokeWidth={1.75}
        strokeOpacity={0.75}
        fill="none"
        {...(reducedMotion ? { strokeDasharray: '6 5' } : {})}
      />
      <path d={link.head} fill="currentColor" />
      {reducedMotion
        ? null
        : Array.from({ length: PIPS_PER_LINK }).map((_, i) => (
            <circle key={i} r={3} fill="currentColor">
              <animateMotion
                dur={`${link.durationSec}s`}
                repeatCount="indefinite"
                begin={`-${((link.durationSec * i) / PIPS_PER_LINK).toFixed(2)}s`}
                path={link.d}
              />
            </circle>
          ))}
    </g>
  );
}

/** One provider box — or a ghost chip, which is the same fact drawn as absence. */
function Node({ node, modeLabel }: { node: DiagramNode; modeLabel: string }) {
  const ghostLike = node.kind === 'ghost' || node.kind === 'nothing';
  const titleSize = ghostLike ? 10 : 11.5;

  return (
    <g
      className={TONE_CLASS[node.tone]}
      data-testid={`integrations-diagram-node-${node.id}`}
      data-kind={node.kind}
      data-tone={node.tone}
      data-inside={node.inside ? 'true' : 'false'}
    >
      {node.vendor ? <title>{node.vendor}</title> : null}
      <rect
        x={node.x}
        y={node.y}
        width={node.w}
        height={node.h}
        rx={7}
        fill={ghostLike ? 'none' : SURFACE}
        stroke="currentColor"
        strokeOpacity={ghostLike ? 0.5 : 0.65}
        strokeWidth={1.25}
        {...(ghostLike || node.kind === 'unknown' ? { strokeDasharray: '4 3' } : {})}
      />
      {ghostLike ? null : (
        <rect
          x={node.x + 1}
          y={node.y + 6}
          width={3}
          height={node.h - 12}
          rx={1.5}
          fill="currentColor"
        />
      )}
      <text
        x={node.x + (ghostLike ? 10 : 14)}
        y={node.y + (ghostLike ? node.h / 2 + 3.5 : 13)}
        fontSize={titleSize}
        fontWeight={ghostLike ? 400 : 600}
        fill={ghostLike ? MUTED : INK}
      >
        {fitText(node.label, node.w - (ghostLike ? 18 : 22), titleSize)}
      </text>
      {ghostLike ? null : (
        <text x={node.x + 14} y={node.y + 25} fontSize={9} fill={MUTED}>
          {fitText(modeLabel, node.w - 22, 9)}
        </text>
      )}
    </g>
  );
}

/** `MOCK · nexa-transport` — the mode word, then who declared it. */
function modeLabelOf(node: DiagramNode, t: TFunction<'network'>): string {
  if (node.kind === 'unknown') {
    return `${t('providers.integrations.unknown.badge')}${node.declaredBy ? ` · ${node.declaredBy}` : ''}`;
  }
  if (node.kind !== 'wired') return '';
  const mode = t(`providers.integrations.mode.${node.tone}`);
  return node.declaredBy ? `${mode} · ${node.declaredBy}` : mode;
}

/**
 * The key to the geometry, in words. The shape carries the argument, but the
 * argument is only self-evident once someone has been told the rule ONCE.
 */
function Legend() {
  const { t } = useTranslation('network');
  const items: { tone: DiagramTone; term: string; gloss: string; dashed: boolean }[] = [
    {
      tone: 'mock',
      term: t('providers.integrations.mode.mock'),
      gloss: t('providers.diagram.legend.mock'),
      dashed: false,
    },
    {
      tone: 'sandbox',
      term: t('providers.integrations.mode.sandbox'),
      gloss: t('providers.diagram.legend.sandbox'),
      dashed: false,
    },
    {
      tone: 'production',
      term: t('providers.integrations.mode.production'),
      gloss: t('providers.diagram.legend.production'),
      dashed: false,
    },
    {
      tone: 'ghost',
      term: t('providers.diagram.legend.ghostTerm'),
      gloss: t('providers.diagram.legend.ghost'),
      dashed: true,
    },
    {
      tone: 'unknown',
      term: t('providers.integrations.unknown.badge'),
      gloss: t('providers.diagram.legend.unknown'),
      dashed: true,
    },
  ];

  return (
    <ul
      className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground"
      data-testid="integrations-diagram-legend"
    >
      {items.map((item) => (
        <li key={item.tone} className="flex min-w-0 items-center gap-1.5">
          <span
            aria-hidden
            className={`inline-block h-0 w-5 shrink-0 border-t-2 ${item.dashed ? 'border-dashed' : ''} ${TONE_CLASS[item.tone]}`}
            style={{ borderTopColor: 'currentColor' }}
          />
          <span className="font-medium text-foreground">{item.term}</span>
          <span>{item.gloss}</span>
        </li>
      ))}
    </ul>
  );
}
